import type { FC, ReactNode } from 'react'
import { useEffect } from 'react'

import { AuthClient, decodeToken, getValidPhoneNumberFromUrl } from '@skytech/auth'
import { config } from '@skytech/config'
import { AppWithMonitoring } from '@skytech/monitoring'
import { CookieConsent, PageLoader } from '@skytech/ui'
import { DEFAULT_LOCALE, getDefaultCountryId, getWhiteLabelBasedOnId } from '@skytech/utils'
import { QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill'
import { IntlProvider } from 'react-intl'
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import MetaTags from '@/app/MetaTags'
import useLogIn from '@/hooks/useLogin'
import Login from '@/modules/login/Login'
import Logout from '@/modules/Logout'
import { commonQueries } from '@/resources/queries'
import { commonTranslationResources } from '@/resources/translations'
import userResources from '@/resources/user'
import { APP_ROUTES } from '@/util/app-routes'
import getUserFromToken from '@/util/getUserFromToken'

import { AuthenticatedRoutes } from './AuthenticatedRoutes'
import { AppContextProvider, useAppContext } from './index.context'
import BurgerMenu from './layout/BurgerMenu'
import { Header } from './layout/Header'
import { NavBar } from './layout/NavBar'
import { queryClient } from './queryClient'
import { useTranslationWithJSXSupport } from './translations/translations-hooks'
import type { TokenData } from './types'

import '@/style/body.scss'
import '@/style/animations.scss'
import '@/style/app.scss'

const AuthenticatedPages: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { setLocale, setWhiteLabel } = useAppContext()
  const { data: user, isLoading: isUserLoading } = useQuery(
    commonQueries.user(),
    userResources.get,
    {
      retry: 0,
      staleTime: Infinity,
      onSuccess(user) {
        const whiteLabel = getWhiteLabelBasedOnId(user.whiteLabelId)
        setWhiteLabel(whiteLabel)
      },
      onError() {
        AuthClient.logOut({ isTriggeredProgrammatically: true })
      },
    }
  )

  const { data: profile, isLoading: isProfileLoading } = useQuery(
    commonQueries.profile(),
    userResources.getProfile,
    {
      enabled: Boolean(user),
      staleTime: Infinity,
      onSuccess(profile) {
        if (profile.locale) setLocale(profile.locale)
      },
    }
  )

  if (isUserLoading || !user || isProfileLoading || !profile) return <PageLoader />

  // Force redirect if user hasn't reset the password
  if (profile.isPasswordChangeRequired && location.pathname !== APP_ROUTES.ACCOUNT_SETTINGS) {
    navigate(`${APP_ROUTES.ACCOUNT_SETTINGS}?missing-reset=true`)
    return null
  }

  return (
    <>
      <Header />
      <NavBar />
      <AuthenticatedRoutes />
      <BurgerMenu />
    </>
  )
}

const AppCore: FC = () => {
  polyfillCountryFlagEmojis()

  const location = useLocation()
  const translateWithJSXSupport = useTranslationWithJSXSupport()
  const navigate = useNavigate()
  const client = useQueryClient()
  const loginMutation = useLogIn({ isUrlCredentials: true })
  const { locale, setWhiteLabel } = useAppContext()

  const searchParams = new URLSearchParams(location.search)
  const urlPassword = searchParams.get('key')
  const urlPhoneNumber = getValidPhoneNumberFromUrl(location.pathname)
  const urlToken = searchParams.get('token')

  useEffect(() => {
    const isUrlCredentials = urlPhoneNumber && urlPassword
    const defaultPhoneCountryId = getDefaultCountryId(locale)

    if (isUrlCredentials) {
      loginMutation.mutate({
        phoneNumber: urlPhoneNumber,
        accessCode: urlPassword,
        phoneCountryId: defaultPhoneCountryId,
      })
    }
    // todo: debug why the mutation isn't memoized when we've bumped the react-query
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPhoneNumber, urlPassword])

  useEffect(() => {
    if (urlToken) {
      AuthClient.setToken(urlToken)

      const tokenData: TokenData = decodeToken(urlToken)
      const user = getUserFromToken(tokenData)

      client.setQueryData(commonQueries.user(), user)
      if (!user) return
      // Needs to be set manually as the hook has not been fired yet
      const whiteLabel = getWhiteLabelBasedOnId(user.whiteLabelId)
      setWhiteLabel(whiteLabel)

      const url = new URL(window.location.href)
      url.searchParams.delete('token')
      navigate(url)
    }
  }, [client, navigate, setWhiteLabel, urlToken])

  const isUrlCredentials = urlPhoneNumber && urlPassword
  if (loginMutation.isLoading || isUrlCredentials || urlToken) return <PageLoader />

  const isDevEnv = config.ENVIRONMENT === 'dev'

  return (
    <>
      <Routes>
        <Route path={APP_ROUTES.LOGIN} element={<Login />} />
        <Route path={APP_ROUTES.LOGOUT} element={<Logout />} />

        <Route path="*" element={<AuthenticatedPages />} />
      </Routes>

      {!isDevEnv && (
        <CookieConsent
          onRejectCookies={() => {
            if (!AuthClient.getToken()) return
            AuthClient.logOut({ isTriggeredProgrammatically: true })
          }}
          onTranslate={translateWithJSXSupport}
        />
      )}
    </>
  )
}

const AppIntl: FC<{ children: ReactNode }> = ({ children }) => {
  const { locale } = useAppContext()

  const { data: translationList, isLoading: isTranslationsLoading } = useQuery(
    commonQueries.translationList(),
    commonTranslationResources.getList,
    { staleTime: Infinity }
  )

  if (isTranslationsLoading) return <PageLoader />

  // todo: add the missing error page
  if (!translationList) throw new Error('Something went wrong when fetching translations.')

  return (
    <IntlProvider
      locale={locale}
      defaultLocale={DEFAULT_LOCALE}
      messages={translationList[locale]}
      // Do not show missing translations errors
      onError={() => {}}
    >
      {children}
    </IntlProvider>
  )
}

export const App: FC = () => (
  <AppWithMonitoring>
    <Router>
      <QueryClientProvider client={queryClient}>
        <AppContextProvider>
          <div id="app" className="theme-mc">
            <AppIntl>
              <AppCore />

              <MetaTags />
            </AppIntl>
          </div>
        </AppContextProvider>

        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Router>
  </AppWithMonitoring>
)
